import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaLink, FaCopy, FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { PATHS } from '../../routes/routes';
import { HOST_API } from '../../api/config';

type Props = {
  drawId?: string;
  packageId?: string;
  isPromo?: boolean;
  className?: string;
};

/**
 * Renders a small info/link icon with a hover tooltip that lists URLs for:
 * - Draw (default + active tracking links)
 * - Promo draw (default + active tracking links)
 * - Package (default)
 * Fetches draw tracking links lazily on first hover to avoid extra requests.
 */
const LinksTooltipCell: React.FC<Props> = ({ drawId, packageId, isPromo = false, className }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<Array<{ id: string; name: string; url: string; slug?: string; clicks?: number; purchases?: number; revenue?: number; isActive?: boolean; }>>([]);
  const [totals, setTotals] = useState<{ clicks: number; purchases: number; revenue: number; } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // placement inferred by whether coords has top or bottom
  const [coords, setCoords] = useState<{ right: number; top?: number; bottom?: number }>({ right: 12, top: 0 });
  const loadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const buildDefaultDrawUrl = useCallback(() => {
    if (!drawId) return '';
    const url = isPromo
      ? PATHS.PROMO_DRAW_SIGNUP.replace(':drawId', drawId)
      : PATHS.DRAW_INFO.replace(':drawId', drawId);
    return origin + url;
  }, [drawId, isPromo, origin]);

  const buildPackageUrl = useCallback(() => {
    if (!packageId) return '';
    return origin + PATHS.PACKAGE_INFO.replace(':packageId', packageId);
  }, [packageId, origin]);

  const ensureData = useCallback(async () => {
    if (loaded || loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
  const rowsAccum: Array<{ id: string; name: string; url: string; slug?: string; clicks?: number; purchases?: number; revenue?: number; isActive?: boolean; }> = [];

      if (drawId) {
        const defaultDrawUrl = buildDefaultDrawUrl();
        if (defaultDrawUrl) {
          rowsAccum.push({ id: 'default', name: 'Default', url: defaultDrawUrl });
        }

        try {
          const res = await fetch(`${HOST_API}/public/club-owner/draw/${drawId}`);
          if (res.ok) {
            const drawData = await res.json();
            const allLinks = Array.isArray(drawData?.links) ? drawData.links : [];
            const active = allLinks.filter((l: any) => l?.isActive);
            const inactive = allLinks.filter((l: any) => !l?.isActive);

            let totalClicks = 0;
            let totalPurchases = 0;
            let totalRevenue = 0;

            const pushLink = (l: any, isActiveFlag: boolean) => {
              const appPath = (isPromo
                ? PATHS.PROMO_DRAW_LINK.replace(':drawId', drawId)
                : PATHS.DRAW_INFO_LINK.replace(':drawId', drawId)
              ).replace(':slug', l.slug);
              const url = origin + appPath;
              const rowId = String(l.id ?? l.slug ?? appPath);
              const clicks = Number(l.clickCount || 0);
              const purchases = Number(l.purchaseCount || 0);
              const revenue = Number(l.totalRevenue || 0);
              totalClicks += clicks;
              totalPurchases += purchases;
              totalRevenue += revenue;
              rowsAccum.push({ id: rowId, name: l.name, slug: l.slug, url, clicks, purchases, revenue, isActive: isActiveFlag });
            };

            // Active first, then inactive
            active.forEach((l: any) => pushLink(l, true));
            inactive.forEach((l: any) => pushLink(l, false));

            if (allLinks.length > 0) setTotals({ clicks: totalClicks, purchases: totalPurchases, revenue: totalRevenue });
          }
        } catch (e: any) {
          // silent: default link row already available
          console.warn('Failed to load tracking links', e);
        }
      }

      if (packageId) {
        const pkgUrl = buildPackageUrl();
        if (pkgUrl) rowsAccum.push({ id: 'package', name: 'Package', url: pkgUrl });
      }

      setRows(rowsAccum);
      setLoaded(true);
    } catch (e: any) {
      setError('Failed to load links');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [loaded, drawId, isPromo, origin, buildDefaultDrawUrl, packageId, buildPackageUrl]);

  const hasAny = useMemo(() => !!(drawId || packageId), [drawId, packageId]);
  if (!hasAny) return null;

  // Close on outside click / escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insidePanel = panelRef.current?.contains(target);
      if (!insideTrigger && !insidePanel) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const computePlacementAndPosition = () => {
    try {
      const el = containerRef.current;
      if (el && typeof window !== 'undefined') {
        const rect = el.getBoundingClientRect();
  // const spaceAbove = rect.top; // not used directly; kept for clarity
        const spaceBelow = window.innerHeight - rect.bottom;
        const desired = 360; // px; conservative estimate
  const nextPlacement = spaceBelow >= Math.min(desired, window.innerHeight * 0.6) ? 'below' : 'above';

        const margin = 8;
        const right = Math.max(margin, window.innerWidth - rect.right);
        if (nextPlacement === 'below') {
          setCoords({ right, top: rect.bottom + margin });
        } else {
          setCoords({ right, bottom: Math.max(margin, window.innerHeight - rect.top + margin) });
        }
      }
    } catch {}
  };

  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      computePlacementAndPosition();
      ensureData();
    }
  };

  // Recompute while open on resize/scroll to avoid clipping
  useEffect(() => {
    if (!open) return;
    const onResize = () => computePlacementAndPosition();
    const onScroll = () => computePlacementAndPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  const copyUrl = async (id: string, url: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId((prev) => (prev === id ? null : prev)), 1200);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  return (
    <div className={`relative inline-block ${className ?? ''}`} ref={containerRef}>
      <button type="button" className="menu-button" aria-label="links" onClick={onToggle}>
        <FaLink className={`text-lg ${open ? 'text-primary' : 'text-gray-500'}`} />
      </button>

      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          className={`fixed z-[99999] w-[420px] max-w-[90vw] bg-base-100 border border-border rounded-box shadow-xl`}
          style={{ right: coords.right, ...(coords.top !== undefined ? { top: coords.top } : { bottom: coords.bottom }) }}
        >
          <div className="px-3 py-2 border-b border-border flex items-center justify-between sticky top-0 bg-base-100">
            <div className="text-sm font-medium">Links</div>
            <button className="btn btn-ghost btn-xs" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="p-3 max-h-96 overflow-auto">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <span className="loading loading-spinner loading-sm" />
              </div>
            )}
            {!loading && error && (
              <div className="text-error text-sm">{error}</div>
            )}
            {!loading && !error && rows.length === 0 && (
              <div className="text-sm text-gray-500">No links available</div>
            )}
            {!loading && !error && rows.length > 0 && (
              <ul className="space-y-3">
                {rows.map((r) => {
                  const hasStats = typeof r.clicks === 'number';
                  const cr = hasStats && r.clicks! > 0 ? ((r.purchases! / r.clicks!) * 100).toFixed(1) : null;
                  return (
                    <li key={r.id} className="border border-border rounded-md p-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm font-medium truncate" title={r.name}>
                          {r.name}{r.slug ? ` (/` + r.slug + `)` : ''}
                          {r.slug && r.isActive === false ? (
                            <span className="ml-2 align-middle text-[10px] uppercase text-gray-500 border border-gray-300 rounded px-1 py-0.5">inactive</span>
                          ) : null}
                        </div>
                        <div className="flex items-center gap-1">
                          <button className="btn btn-ghost btn-xs" onClick={() => copyUrl(r.id, r.url)}>
                            {copiedId === r.id ? 'Copied' : <><FaCopy className="inline mr-1" />Copy</>}
                          </button>
                          <a className="btn btn-primary btn-xs" href={r.url} target="_blank" rel="noopener noreferrer">
                            <FaArrowUpRightFromSquare className="inline mr-1" />Open
                          </a>
                        </div>
                      </div>
                      <div className="text-xs text-gray-600 truncate" title={r.url}>{r.url}</div>
                      {hasStats && (
                        <div className="mt-1 text-xs text-gray-700">
                          Clicks {r.clicks} | Purchases {r.purchases} | Revenue {Number(r.revenue || 0).toFixed(2)}{cr !== null ? ` | CR ${cr}%` : ''}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {totals && (
            <div className="px-3 py-2 border-t border-border text-xs text-gray-700">
              Totals: Clicks {totals.clicks} | Purchases {totals.purchases} | Revenue {totals.revenue.toFixed(2)} | CR {totals.clicks > 0 ? ((totals.purchases / totals.clicks) * 100).toFixed(1) : '0.0'}%
            </div>
          )}
        </div>, document.body)
      }
    </div>
  );
};

export default LinksTooltipCell;
